import type {
  SafetyFunctionWithArchitecture,
  SistemaArchitectureNode,
  SistemaChannelWithNodes,
  SistemaComponentWithChannels,
} from '@/shared/hooks/api/machineryDirectiveCompliance'
import {
  ArrowRight,
  MoreHorizontal,
  PencilIcon,
  PlusIcon,
  TrashIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/shared/lib/utils'

type ArchitectureDialogState =
  | { kind: 'component-create' }
  | { kind: 'component-edit'; component: SistemaComponentWithChannels }
  | { kind: 'component-delete'; componentId: string }
  | { kind: 'channel-create'; componentId: string }
  | { kind: 'channel-edit'; channel: SistemaChannelWithNodes }
  | { kind: 'channel-delete'; channelId: string }
  | { kind: 'block-create'; channelId: string }
  | { kind: 'block-edit'; node: SistemaArchitectureNode }
  | { kind: 'block-delete'; nodeId: string }
  | { kind: 'element-create'; channelId: string; parentNodeId: string }
  | { kind: 'element-edit'; node: SistemaArchitectureNode }
  | { kind: 'element-delete'; nodeId: string }
  | null

export type { ArchitectureDialogState }

const FUNCTION_TYPE_ORDER: Record<string, number> = {
  fncInput: 0,
  fncLogic: 1,
  fncOutput: 2,
}

export function orderSistemaComponents(
  components: SistemaComponentWithChannels[]
): SistemaComponentWithChannels[] {
  return [...components].sort((first, second) => {
    const firstOrder =
      first.functiontypes != null
        ? FUNCTION_TYPE_ORDER[first.functiontypes] ?? 99
        : 99
    const secondOrder =
      second.functiontypes != null
        ? FUNCTION_TYPE_ORDER[second.functiontypes] ?? 99
        : 99
    if (firstOrder !== secondOrder) {
      return firstOrder - secondOrder
    }
    return first.sortOrder - second.sortOrder
  })
}

export type SistemaArchitectureCounts = {
  components: number
  channels: number
  blocks: number
  elements: number
}

export function countSistemaArchitecture(
  safetyFunction: SafetyFunctionWithArchitecture
): SistemaArchitectureCounts {
  const components = safetyFunction.sistemaComponents ?? []
  let channels = 0
  let blocks = 0
  let elements = 0
  for (const component of components) {
    channels += component.sistemaChannels.length
    for (const channel of component.sistemaChannels) {
      for (const node of channel.sistemaArchitectureNodes) {
        blocks += 1
        elements += node.childNodes?.length ?? 0
      }
    }
  }
  return {
    components: components.length,
    channels,
    blocks,
    elements,
  }
}

function enumLabel(
  translate: (key: string, options?: Record<string, unknown>) => string,
  group: string,
  value: string | null
) {
  if (value == null || value.trim() === '') {
    return null
  }
  return translate(`safetyFunctionsPage.enums.${group}.${value}`, {
    defaultValue: value,
  })
}

function ArrowConnector() {
  return (
    <div className='flex items-center px-1'>
      <ArrowRight className='size-5 text-muted-foreground rtl:rotate-180' />
    </div>
  )
}

function ElementPill({
  element,
  isMutationPending,
  onOpenDialog,
}: {
  element: SistemaArchitectureNode
  isMutationPending: boolean
  onOpenDialog: (state: NonNullable<ArchitectureDialogState>) => void
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  return (
    <div className='flex items-center justify-between gap-1 rounded border bg-background px-2 py-1'>
      <span className='truncate text-xs'>{element.name}</span>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='icon'
            className='size-6 shrink-0'
            disabled={isMutationPending}
          >
            <MoreHorizontal className='size-3' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuItem
            onClick={() => onOpenDialog({ kind: 'element-edit', node: element })}
          >
            <PencilIcon />
            {t('safetyFunctionsPage.actions.edit')}
          </DropdownMenuItem>
          <DropdownMenuItem
            variant='destructive'
            className='text-destructive focus:text-destructive'
            onClick={() =>
              onOpenDialog({ kind: 'element-delete', nodeId: element.id })
            }
          >
            <TrashIcon />
            {t('safetyFunctionsPage.actions.delete')}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}

function BlockChip({
  block,
  channelId,
  isMutationPending,
  onOpenDialog,
}: {
  block: SistemaArchitectureNode
  channelId: string
  isMutationPending: boolean
  onOpenDialog: (state: NonNullable<ArchitectureDialogState>) => void
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const elements = block.childNodes ?? []

  return (
    <div className='space-y-2 rounded-md border bg-muted/40 p-2'>
      <div className='flex items-center justify-between gap-1'>
        <div className='flex min-w-0 items-center gap-1.5'>
          <Badge variant='outline' className='shrink-0 bg-background font-normal'>
            {t('safetyFunctionsPage.architecture.block')}
          </Badge>
          <span className='truncate text-sm font-medium'>{block.name}</span>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='size-7 shrink-0'
              disabled={isMutationPending}
            >
              <MoreHorizontal className='size-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuItem
              onClick={() => onOpenDialog({ kind: 'block-edit', node: block })}
            >
              <PencilIcon />
              {t('safetyFunctionsPage.actions.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                onOpenDialog({
                  kind: 'element-create',
                  channelId,
                  parentNodeId: block.id,
                })
              }
            >
              <PlusIcon />
              {t('safetyFunctionsPage.architecture.addElement')}
            </DropdownMenuItem>
            <DropdownMenuItem
              variant='destructive'
              className='text-destructive focus:text-destructive'
              onClick={() =>
                onOpenDialog({ kind: 'block-delete', nodeId: block.id })
              }
            >
              <TrashIcon />
              {t('safetyFunctionsPage.actions.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {elements.length > 0 ? (
        <div className='space-y-1'>
          {elements.map((element) => (
            <ElementPill
              key={element.id}
              element={element}
              isMutationPending={isMutationPending}
              onOpenDialog={onOpenDialog}
            />
          ))}
        </div>
      ) : null}
    </div>
  )
}

function ChannelLane({
  channel,
  isMutationPending,
  onOpenDialog,
}: {
  channel: SistemaChannelWithNodes
  isMutationPending: boolean
  onOpenDialog: (state: NonNullable<ArchitectureDialogState>) => void
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const channelTypeLabel = enumLabel(t, 'channeltype', channel.channeltype)
  const blocks = channel.sistemaArchitectureNodes

  return (
    <div className='space-y-2 rounded-md border border-dashed p-2'>
      <div className='flex items-center justify-between gap-1'>
        <div className='min-w-0'>
          <p className='truncate text-sm font-medium'>{channel.name}</p>
          {channelTypeLabel ? (
            <p className='truncate text-xs text-muted-foreground'>
              {channelTypeLabel}
            </p>
          ) : null}
        </div>
        <div className='flex shrink-0 items-center gap-1'>
          <Button
            type='button'
            variant='ghost'
            size='sm'
            disabled={isMutationPending}
            onClick={() =>
              onOpenDialog({ kind: 'block-create', channelId: channel.id })
            }
          >
            <PlusIcon className='mr-1 size-3' />
            {t('safetyFunctionsPage.architecture.addBlock')}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant='ghost'
                size='icon'
                className='size-7'
                disabled={isMutationPending}
              >
                <MoreHorizontal className='size-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem
                onClick={() =>
                  onOpenDialog({ kind: 'channel-edit', channel })
                }
              >
                <PencilIcon />
                {t('safetyFunctionsPage.actions.edit')}
              </DropdownMenuItem>
              <DropdownMenuItem
                variant='destructive'
                className='text-destructive focus:text-destructive'
                onClick={() =>
                  onOpenDialog({ kind: 'channel-delete', channelId: channel.id })
                }
              >
                <TrashIcon />
                {t('safetyFunctionsPage.actions.delete')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      {blocks.length > 0 ? (
        <div className='space-y-2'>
          {blocks.map((block) => (
            <BlockChip
              key={block.id}
              block={block}
              channelId={channel.id}
              isMutationPending={isMutationPending}
              onOpenDialog={onOpenDialog}
            />
          ))}
        </div>
      ) : (
        <p className='text-xs text-muted-foreground'>
          {t('safetyFunctionsPage.architecture.noBlocks')}
        </p>
      )}
    </div>
  )
}

function SubsystemCard({
  component,
  isMutationPending,
  onOpenDialog,
}: {
  component: SistemaComponentWithChannels
  isMutationPending: boolean
  onOpenDialog: (state: NonNullable<ArchitectureDialogState>) => void
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const catLabel = enumLabel(t, 'cat', component.cat)
  const functionTypeLabel = enumLabel(t, 'functiontypes', component.functiontypes)
  const channels = component.sistemaChannels

  return (
    <div className='flex w-72 shrink-0 flex-col rounded-lg border bg-card'>
      <div className='flex items-start justify-between gap-1 border-b p-3'>
        <div className='min-w-0 space-y-1'>
          <p className='truncate font-medium'>{component.name}</p>
          <div className='flex flex-wrap items-center gap-1'>
            {catLabel ? (
              <Badge variant='secondary' className='font-normal'>
                {catLabel}
              </Badge>
            ) : null}
            {functionTypeLabel ? (
              <Badge variant='outline' className='font-normal'>
                {functionTypeLabel}
              </Badge>
            ) : null}
            {component.missiontime ? (
              <span className='text-xs text-muted-foreground'>
                {component.missiontime}
              </span>
            ) : null}
          </div>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant='ghost'
              size='icon'
              className='size-8 shrink-0'
              disabled={isMutationPending}
            >
              <MoreHorizontal className='size-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align='end'>
            <DropdownMenuItem
              onClick={() =>
                onOpenDialog({ kind: 'component-edit', component })
              }
            >
              <PencilIcon />
              {t('safetyFunctionsPage.actions.edit')}
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() =>
                onOpenDialog({
                  kind: 'channel-create',
                  componentId: component.id,
                })
              }
            >
              <PlusIcon />
              {t('safetyFunctionsPage.architecture.addChannel')}
            </DropdownMenuItem>
            <DropdownMenuItem
              variant='destructive'
              className='text-destructive focus:text-destructive'
              onClick={() =>
                onOpenDialog({
                  kind: 'component-delete',
                  componentId: component.id,
                })
              }
            >
              <TrashIcon />
              {t('safetyFunctionsPage.actions.delete')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <div className='flex-1 space-y-2 p-3'>
        {channels.length > 0 ? (
          channels.map((channel) => (
            <ChannelLane
              key={channel.id}
              channel={channel}
              isMutationPending={isMutationPending}
              onOpenDialog={onOpenDialog}
            />
          ))
        ) : (
          <p className='text-xs text-muted-foreground'>
            {t('safetyFunctionsPage.architecture.noChannels')}
          </p>
        )}
        <Button
          type='button'
          variant='outline'
          size='sm'
          className='w-full'
          disabled={isMutationPending}
          onClick={() =>
            onOpenDialog({ kind: 'channel-create', componentId: component.id })
          }
        >
          <PlusIcon className='mr-1 size-3' />
          {t('safetyFunctionsPage.architecture.addChannel')}
        </Button>
      </div>
    </div>
  )
}

export type SistemaBlockDiagramProps = {
  safetyFunction: SafetyFunctionWithArchitecture
  isMutationPending: boolean
  onOpenDialog: (state: NonNullable<ArchitectureDialogState>) => void
}

export function SistemaBlockDiagram({
  safetyFunction,
  isMutationPending,
  onOpenDialog,
}: SistemaBlockDiagramProps) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const components = orderSistemaComponents(safetyFunction.sistemaComponents ?? [])

  if (components.length === 0) {
    return (
      <div className='rounded-lg border border-dashed p-6 text-center'>
        <p className='text-sm text-muted-foreground'>
          {t('safetyFunctionsPage.architecture.empty')}
        </p>
      </div>
    )
  }

  return (
    <div className='space-y-3'>
      <ScrollArea orientation='horizontal' className='w-full pb-3'>
        <div className='flex w-max items-stretch'>
          {components.map((component, index) => (
            <div key={component.id} className='flex items-stretch'>
              <SubsystemCard
                component={component}
                isMutationPending={isMutationPending}
                onOpenDialog={onOpenDialog}
              />
              {index < components.length - 1 ? <ArrowConnector /> : null}
            </div>
          ))}
        </div>
      </ScrollArea>
    </div>
  )
}

export function SistemaBlockDiagramThumbnail({
  safetyFunction,
  className,
}: {
  safetyFunction: SafetyFunctionWithArchitecture
  className?: string
}) {
  const { t } = useTranslation('machineryDirectiveCompliance')
  const components = orderSistemaComponents(safetyFunction.sistemaComponents ?? [])

  if (components.length === 0) {
    return (
      <p className={cn('text-xs text-muted-foreground', className)}>
        {t('safetyFunctionsPage.architecture.emptyShort')}
      </p>
    )
  }

  return (
    <div className={cn('flex items-center gap-1 overflow-hidden', className)}>
      {components.map((component, index) => {
        const functionTypeAbbr =
          component.functiontypes === 'fncInput'
            ? t('safetyFunctionsPage.enums.functiontypes.fncInput', {
                defaultValue: 'I',
              })
            : component.functiontypes === 'fncLogic'
              ? t('safetyFunctionsPage.enums.functiontypes.fncLogic', {
                  defaultValue: 'L',
                })
              : component.functiontypes === 'fncOutput'
                ? t('safetyFunctionsPage.enums.functiontypes.fncOutput', {
                    defaultValue: 'O',
                  })
                : null
        const catLabel =
          component.cat != null && component.cat.trim() !== ''
            ? t(`safetyFunctionsPage.enums.cat.${component.cat}`, {
                defaultValue: component.cat,
              })
            : null
        return (
        <div key={component.id} className='flex items-center gap-1'>
          <div className='rounded border bg-muted/40 px-2 py-1'>
            <p className='max-w-20 truncate text-[10px] font-medium leading-tight'>
              {component.name}
            </p>
            {functionTypeAbbr != null || catLabel != null ? (
              <p className='truncate text-[9px] text-muted-foreground leading-tight'>
                {[functionTypeAbbr, catLabel].filter(Boolean).join(' · ')}
              </p>
            ) : null}
            <div className='mt-0.5 flex flex-col gap-0.5'>
              {component.sistemaChannels.slice(0, 3).map((channel) => (
                <span
                  key={channel.id}
                  className='h-1 w-8 rounded-full bg-primary/40'
                />
              ))}
            </div>
          </div>
          {index < components.length - 1 ? (
            <ArrowRight className='size-3 shrink-0 text-muted-foreground rtl:rotate-180' />
          ) : null}
        </div>
        )
      })}
    </div>
  )
}
